import React from 'react';
import { observer } from 'mobx-react';
import { observable, flow, reaction } from 'mobx';
import { Label } from 'reactstrap';
import { AvForm, AvGroup, AvInput } from 'availity-mobx-reactstrap-validation';
import { Spring, animated } from 'react-spring';

import analytics from 'js/services/Analytics';
import { makeRequest, errorResponse, navigate, url } from 'js/services/Network';
import { lightGrey1Color } from 'js/config/style';

import PageComponent from 'js/components/pages/PageComponent';
import AppContent from 'js/components/AppContent';
import Backdrop from 'js/components/core/Backdrop';
import Constraint from 'js/components/Constraint';
import AppUser from 'js/stores/AppUser';

import PageTitle from './PageTitle';
import Message from './Message';
import Icon from './Icon';
import Error from './Error';
import Button from './Button';

@observer
export default class ChangePasswordPage extends PageComponent {
  pageName = 'ChangePasswordPage';
  pageColor = lightGrey1Color;
  loginRequired = true;

  @observable requestInProgress = false;
  @observable wasSuccessful = false;
  @observable error = null;

  pageEvent = () => { /*analytics.page('Change Password')*/ };

  constructor(props) {
    super(props);
    this.loggedInReaction = reaction(() => AppUser.isLoggedIn, isLoggedIn => isLoggedIn && navigate(url('home-page')));
  }

  componentWillUnmount() {
    this.loggedInReaction();
  }

  submit = flow(function*(formValues) {
    this.error = null;

    if (!this.requestInProgress) {
      this.requestInProgress = true;

      try {
        yield makeRequest('change-password', null, formValues);
        //analytics.track('Password Changed');
        this.wasSuccessful = true;
      } catch (error) {
        this.error = errorResponse(error);
      }

      this.requestInProgress = false;
    }
  });

  renderPage() {
    const { requestInProgress, error } = this;
    const offset = this.wasSuccessful ? '0rem' : '3rem';

    return (
      <AppContent padded>
        <Backdrop offset={offset}>
          <Constraint>
            <PageTitle>Change password</PageTitle>
          </Constraint>
        </Backdrop>

        <Constraint offset={offset}>
          {this.wasSuccessful ? (
            <Spring native reset from={{ opacity: 0 }} to={{ opacity: 1 }}>
              {animation => (
                <animated.div style={animation}>
                  <Message>
                    <Icon name="check" />
                    <div>Your password has been changed successfully.</div>
                  </Message>
                </animated.div>
              )}
            </Spring>
          ) : (
            <Spring native from={{ opacity: 0 }} to={{ opacity: 1 }}>
              {animation => (
                <animated.div style={animation}>
                  <AvForm onValidSubmit={(event, values) => this.submit(values)}>
                    <AvGroup>
                      <Label for="old_password">Old password</Label>
                      <AvInput type="password" name="old_password" id="old_password" required />
                    </AvGroup>

                    <AvGroup>
                      <Label for="new_password">New password</Label>
                      <AvInput type="password" name="new_password" id="new_password" maxLength={72} required minLength={6} />
                    </AvGroup>

                    <AvGroup>
                      <Label for="new_password_confirmation">New password confirmation</Label>
                      <AvInput
                        type="password"
                        name="new_password_confirmation"
                        id="new_password_confirmation"
                        maxLength={72}
                        required
                        validate={{ match: { value: 'new_password' } }}
                      />
                    </AvGroup>

                    {error && <Error>{error}</Error>}

                    <Button type="submit" isLoading={requestInProgress}>
                      {requestInProgress ? 'Changing your password ...' : 'Change password now'}
                    </Button>
                  </AvForm>
                </animated.div>
              )}
            </Spring>
          )}
        </Constraint>
      </AppContent>
    );
  }
}
